// src/tour/triggers.js
//
// Decides WHEN a tour is allowed to fire. The tour engine itself (TourContext)
// is dumb about timing — these functions are the policy layer.
//
// v1 wiring status:
//   - shouldFireOnSignup       → WIRED (Dashboard first authenticated mount)
//   - shouldFireOnManualLaunch → WIRED (TourLauncher "Start")
//   - shouldFireOnEmptyHome    → DEFINED, NOT WIRED (feature-flag off, v1.1)
//   - shouldFireOnPostWizard   → DEFINED, NOT WIRED (feature-flag off, v1.1)
//
// Feature flags: default to the constant below, but allow an owner override
// stored in owner_config under key 'tour_triggers' (a JSON object). This lets
// triggers be flipped without a deploy once the surfaces are ready.

import { supabase } from '../supabase'

export const TOUR_TRIGGERS = Object.freeze({
  signup: true,
  manual: true,
  post_wizard: false,  // v1.1
  empty_home: false,   // v1.1
})

let _flagCache = null

/** Resolve effective trigger flags (owner_config override merged over defaults). */
export async function getTriggerFlags() {
  if (_flagCache) return _flagCache
  let flags = { ...TOUR_TRIGGERS }
  try {
    const { data, error } = await supabase
      .from('owner_config')
      .select('value')
      .eq('key', 'tour_triggers')
      .maybeSingle()
    if (!error && data?.value && typeof data.value === 'object') {
      flags = { ...flags, ...data.value }
    }
  } catch {
    // owner_config not reachable (e.g. RLS) — fall back to defaults silently.
  }
  _flagCache = flags
  return flags
}

// Has the user already finished or dismissed this tour? (global = home_id null,
// or any home if homeId omitted). Used to avoid re-firing.
async function hasResolvedTour(userId, tourId, { homeId = undefined } = {}) {
  if (!userId) return true // no user → treat as "don't fire"
  let q = supabase
    .from('tour_progress')
    .select('id, completed_at, dismissed_at, home_id')
    .eq('user_id', userId)
    .eq('tour_id', tourId)
  if (homeId !== undefined) {
    q = homeId == null ? q.is('home_id', null) : q.eq('home_id', homeId)
  }
  const { data, error } = await q
  if (error) {
    console.warn('[tour/triggers] tour_progress read failed:', error.message)
    return true // fail closed — don't nag the user on a transient error
  }
  return (data || []).some(r => r.completed_at || r.dismissed_at)
}

/**
 * Fire the welcome tour for a brand-new user on their first authenticated
 * arrival. True unless they've already completed or dismissed it.
 */
export async function shouldFireOnSignup(userId, tourId = 'welcome') {
  const flags = await getTriggerFlags()
  if (!flags.signup) return false
  return !(await hasResolvedTour(userId, tourId))
}

/**
 * Per-home empty-state trigger. DEFINED, NOT WIRED in v1 (flag off).
 * Checks dismissal scoped to a specific home.
 */
export async function shouldFireOnEmptyHome(userId, homeId, tourId = 'welcome') {
  const flags = await getTriggerFlags()
  if (!flags.empty_home) return false
  return !(await hasResolvedTour(userId, tourId, { homeId }))
}

/**
 * Re-offer after the user finishes another wizard. DEFINED, NOT WIRED in v1.
 * Same resolution logic as signup, different entry point.
 */
export async function shouldFireOnPostWizard(userId, tourId = 'welcome') {
  const flags = await getTriggerFlags()
  if (!flags.post_wizard) return false
  return !(await hasResolvedTour(userId, tourId))
}

/**
 * Manual launch from the Help & Tours launcher. Always allowed (the user
 * explicitly asked), subject only to the manual flag.
 */
export async function shouldFireOnManualLaunch(/* userId, tourId */) {
  const flags = await getTriggerFlags()
  return !!flags.manual
}
